import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Preloader from '../common/Preloader';
import { getDashboard, recordLoginTime, recordLogoutTime, getTimeEntries, requestTimeChange } from '../../services/employeeService';
import { formatHours } from '../../utils/format';
import { formatDate } from '../../utils/format';
import TimeGraph from '../common/TimeGraph';
import VerifyPasswordModal from '../common/VerifyPasswordModal';
import { connectSocket, disconnectSocket } from '../../services/socket';
import { getHolidays } from '../../services/adminService'; // Reuse this for fetching holidays
import { useAuth } from '../../context/AuthContext';
import './EmployeeDashboard.css';

const getTimeString = (dateStr, defaultTime) => {
  if (!dateStr) return defaultTime;
  const d = new Date(dateStr);
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const combineDateAndTime = (dateVal, timeStr) => {
  if (!timeStr) return null;
  const d = new Date(dateVal);
  const [hours, minutes] = timeStr.split(':');
  d.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
  return d.toISOString();
};

const EmployeeDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clocking, setClocking] = useState(false);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [setShowSalary] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(null); // 'in' or 'out'
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestData, setRequestData] = useState({ entryId: '', loginTime: '', logoutTime: '', reason: '' });
  
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [holidays, setHolidays] = useState([]);
  const [showAllRecords, setShowAllRecords] = useState(false);
  
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Graph state
  const [graphEntries, setGraphEntries] = useState([]);
  const [graphRange, setGraphRange] = useState({ start: new Date(), end: new Date() });

  useEffect(() => {
    loadDashboard();
    loadHolidays();
    loadGraphData(filterMonth, filterYear);

    // Connect to socket for live updates
    const socket = connectSocket();
    if (socket) {
      socket.on('notification', (data) => {
        if (data.type === 'attendanceUpdated') {
          // If the update is for this employee (payload.employeeId)
          if (user && String(data.payload.employeeId) === String(user._id || user.id)) {
            console.log('Attendance updated by admin, refreshing...');
            loadDashboard();
            loadGraphData(filterMonth, filterYear);
          }
        }
      });
    }
    return () => {
      disconnectSocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMonth, filterYear, user]);

  // Reset showAllRecords when month/year changes
  useEffect(() => {
    setShowAllRecords(false);
  }, [filterMonth, filterYear]);

  const loadHolidays = async () => {
    try {
      const list = await getHolidays();
      setHolidays(list);
    } catch (e) {
      console.error(e);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const calculateDateRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    return { start, end };
  };

  const loadGraphData = async (m, y) => {
    try {
      const start = new Date(y, m - 1, 1);
      const end = new Date(y, m, 0, 23, 59, 59);

      setGraphRange({ start, end });
      const entries = await getTimeEntries(m, y);
      setGraphEntries(entries);
    } catch (err) {
      console.error('Failed to load graph data:', err);
    }
  };

  // Removed handleDateFilterChange as we now use month/year select

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const data = await getDashboard();
      setDashboardData(data);
      // derive clock status from API (activeEntry)
      if (data && data.activeEntry && !data.activeEntry.logoutTime) {
        setIsClockedIn(true);
      } else {
        setIsClockedIn(false);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async () => {
    try {
      setClocking(true);
      setError('');
      await recordLoginTime();
      setIsClockedIn(true);
      loadDashboard();
      loadGraphData(filterMonth, filterYear);
      setShowConfirm(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setClocking(false);
    }
  };

  const handleClockOut = async () => {
    try {
      setClocking(true);
      setError('');
      await recordLogoutTime();
      setIsClockedIn(false);
      loadDashboard();
      loadGraphData(filterMonth, filterYear);
      setShowConfirm(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setClocking(false);
    }
  };

  const handleRequestChange = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        entryId: requestData.entryId,
        date: requestData.entryDate,
        requestedLogin: combineDateAndTime(requestData.entryDate, requestData.requestedLoginTime),
        requestedLogout: combineDateAndTime(requestData.entryDate, requestData.requestedLogoutTime),
        reason: requestData.reason
      };
      await requestTimeChange(payload);
      setShowRequestModal(false);
      loadDashboard();
      loadGraphData(filterMonth, filterYear);
      alert('Time change request submitted successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openRequestModal = (entry) => {
    setRequestData({
      entryId: entry._id || null,
      entryDate: entry.date,
      originalLogin: entry.loginTime || null,
      originalLogout: entry.logoutTime || null,
      requestedLoginTime: getTimeString(entry.loginTime, "10:00"),
      requestedLogoutTime: getTimeString(entry.logoutTime, "19:00"),
      reason: ''
    });
    setShowRequestModal(true);
  };

  const getDaysInMonth = (m, y) => {
    return new Date(y, m, 0).getDate();
  };

  const generateCalendarRows = () => {
    const daysInMonth = getDaysInMonth(filterMonth, filterYear);
    const rows = [];

    for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(filterYear, filterMonth - 1, day);
        const dayOfWeek = date.getDay(); // 0 = Sunday

        const displayDate = `${filterYear}-${String(filterMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

        const entry = graphEntries.find(e => {
            const eDate = new Date(e.date);
            return eDate.getDate() === day && eDate.getMonth() === (filterMonth - 1) && eDate.getFullYear() === filterYear;
        });

        const holiday = holidays.find(h => {
            const hDate = new Date(h.date);
            return hDate.getDate() === day && hDate.getMonth() === (filterMonth - 1) && hDate.getFullYear() === filterYear;
        });

        const now = new Date();
        now.setHours(0, 0, 0, 0); 
        const currentRowDate = new Date(filterYear, filterMonth - 1, day);
        const isFuture = currentRowDate > now;

        let timeInText = 'Absent';
        let timeOutText = 'Absent';
        let isAbsent = false;

        if (entry) {
            timeInText = formatTime(entry.loginTime);
            if (entry.logoutTime) {
                timeOutText = formatTime(entry.logoutTime);
            } else if (!isFuture && currentRowDate < now) {
                timeOutText = '11:59 PM (Auto)';
            } else {
                timeOutText = '---';
            }
        } else {
            if (isFuture) {
                timeInText = '-';
                timeOutText = '-';
            } else if (dayOfWeek === 0 || holiday) {
                timeInText = ''; 
                timeOutText = '';
            } else {
                isAbsent = true; 
            }
        }

        let rowContent = {
            date: displayDate,
            dayNum: day,
            isSunday: dayOfWeek === 0,
            isHoliday: !!holiday,
            holidayName: holiday ? holiday.name : '',
            clockIn: timeInText,
            clockOut: timeOutText,
            isAbsent: isAbsent,
            hasEntry: !!entry,
            entry: entry,
            isPending: entry?.changeRequest?.isRequested && entry.changeRequest.status === 'pending'
        };

        rows.push(rowContent);
    }
    return rows;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
  };

  const getLiveTodayHours = () => {
    if (!dashboardData || !dashboardData.activeEntry) return '00:00:00 hrs';
    
    if (isClockedIn && dashboardData.activeEntry.loginTime) {
      const login = new Date(dashboardData.activeEntry.loginTime);
      const diffMs = currentTime - login;
      if (diffMs < 0) return '00:00:00 hrs';
      
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
      
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} hrs`;
    } else if (dashboardData.activeEntry.hoursWorked) {
      const totalSeconds = Math.floor(dashboardData.activeEntry.hoursWorked * 3600);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} hrs`;
    }
    
    return '00:00:00 hrs';
  };

  if (loading) {
    return <Preloader />;
  }

  if (!dashboardData) {
    return <div className="error-message">Failed to load dashboard data</div>;
  }

  const { employee, monthlyStats, tasks } = dashboardData;

  return (
    <div className="employee-dashboard">
      <div className="dashboard-header-top">
        <h1>Employee Dashboard</h1>
        <button
          onClick={() => setShowConfirm(isClockedIn ? 'out' : 'in')}
          disabled={clocking}
          className={`btn-top-clock ${isClockedIn ? 'is-clocked-in' : 'is-clocked-out'}`}
          title={isClockedIn ? "Click to Clock Out" : "Click to Clock In"}
        >
          <span className="clock-dot"></span>
          <span className="clock-btn-text">{clocking ? 'Processing...' : (isClockedIn ? 'Clock Out' : 'Clock In')}</span>
        </button>
      </div>

      {error && !showConfirm && <div className="error-message">{error}</div>}

      {/* TOP ROW - 3 EQUAL COLUMNS: Personal | Time | Stats */}
      <div className="top-row">
        {/* Personal Information */}
        <div className="dashboard-card info-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>Personal Information</h2>
            <Link to="/employee/personal-information" className="btn-all-requests" style={{ padding: '4px 12px', fontSize: '0.85rem' }}>
              View All
            </Link>
          </div>
          <div className="info-item">
            <span className="info-label">Name:</span>
            <span className="info-value">{employee.name}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Email:</span>
            <span className="info-value email-text">{employee.email}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Employee Type:</span>
            <span className="info-value" style={{ fontSize: '0.85rem' }}>{employee.employeeType || 'Full Time'}</span>
          </div>
          <div className="info-item designation-item">
            <span className="info-label">Designation:</span>
            <span 
              className="designation-badge"
              title={employee.designation || 'Employee'}
            >
              {employee.designation || 'Employee'}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Work Hours/Day:</span>
            <span className="info-value">{employee.workHours} hrs</span>
          </div>
        </div>

        {/* Time Tracking */}
        <div className="dashboard-card clock-card">
          <h2>Time Tracking</h2>
          <div className="digital-clock animated-gradient-clock" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontSize: '1rem', color: '#6b7280', fontWeight: '500', marginBottom: '0.25rem', letterSpacing: '1px', textTransform: 'uppercase' }}>Current Time</div>
            <div>{currentTime.toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div>
          </div>
          <div className="clock-status">
            {isClockedIn ? (
              <div className="clocked-in">
                <p className="status-text">You are currently clocked in</p>
                <button
                  onClick={() => setShowConfirm('out')}
                  disabled={clocking}
                  className="btn-clock-out"
                >
                  {clocking ? 'Clocking out...' : 'Clock Out'}
                </button>
              </div>
            ) : (
              <div className="clocked-out">
                <p className="status-text">{dashboardData?.activeEntry ? 'You are currently clocked out' : 'You have not clocked in yet'}</p>
                <button
                  onClick={() => setShowConfirm('in')}
                  disabled={clocking}
                  className="btn-clock-in"
                >
                  {clocking ? 'Clocking in...' : 'Clock In'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Monthly Statistics */}
        <div className="dashboard-card stats-card">
          <h2>Monthly Statistics</h2>
          <div className="stat-item">
            <span className="stat-label">Today's Hours Worked:</span>
            <span className="stat-value">{getLiveTodayHours()}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Hours Worked:</span>
            <span className="stat-value">{formatHours(monthlyStats.totalHours)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Days Worked:</span>
            <span className="stat-value">{monthlyStats.daysWorked} days</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Month:</span>
            <span className="stat-value">
              {new Date(monthlyStats.currentYear, monthlyStats.currentMonth - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
            </span>
          </div>
        </div>
      </div>

      {/* BOTTOM ROW - Full Width Tasks */}
      <div className="bottom-row">
        <div className="dashboard-card tasks-card">
          <h2>Recent Tasks</h2>
          {tasks && tasks.length > 0 ? (
            <ul className="tasks-list">
              {tasks.slice(0, 2).map((task) => (
                <li key={task._id} className="task-item">
                  <div className="task-title">{task.title}</div>
                  <div className="task-meta">
                    <span className={`task-status status-${task.status}`}>
                      {task.status}
                    </span>
                    {task.dueDate && (
                      <span className="task-due">
                        Due: {formatDate(task.dueDate)}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="no-tasks">No tasks assigned</p>
          )}
        </div>
      </div>

      {/* Time Graph Section */}
      <TimeGraph
        entries={graphEntries}
        startDate={graphRange.start}
        endDate={graphRange.end}
      />

      {/* Confirmation Modals */}
      {showConfirm && (
        <div className="modal-overlay">
          <div className="modal-content confirmation-modal">
            <h3>Confirm {showConfirm === 'in' ? 'Clock In' : 'Clock Out'}</h3>
            {error ? (
              <>
                <p className="error-message" style={{ margin: '1rem 0', color: 'red', textAlign: 'center', fontWeight: 'bold' }}>{error}</p>
                <div className="modal-actions">
                  <button className="btn-no" onClick={() => { setShowConfirm(null); setError(''); }}>Close</button>
                </div>
              </>
            ) : (
              <>
                <p>Are you sure you want to {showConfirm === 'in' ? 'Clock In' : 'Clock Out'} now?</p>
                <div className="modal-actions">
                  <button className="btn-yes" onClick={showConfirm === 'in' ? handleClockIn : handleClockOut} disabled={clocking}>
                    {clocking ? 'Wait...' : 'Yes'}
                  </button>
                  <button className="btn-no" onClick={() => setShowConfirm(null)} disabled={clocking}>No</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Time Change Request Modal */}
      {showRequestModal && (
        <div className="modal-overlay">
          <div className="modal-content request-modal">
            <h3>Request Time Change</h3>
            <div className="request-modal-date" style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: '500', color: '#374151' }}>
              Date: <strong>{new Date(requestData.entryDate).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</strong>
            </div>

            <div className="original-times-summary" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', padding: '0.75rem', backgroundColor: '#f3f4f6', borderRadius: '6px', marginBottom: '1.25rem' }}>
              <div>
                <span style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 'bold' }}>Original In</span>
                <span style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1f2937' }}>{formatTime(requestData.originalLogin)}</span>
              </div>
              <div>
                <span style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 'bold' }}>Original Out</span>
                <span style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1f2937' }}>{formatTime(requestData.originalLogout)}</span>
              </div>
            </div>

            <form onSubmit={handleRequestChange}>
              <div className="form-group">
                <label>Requested Login Time:</label>
                <input
                  type="time"
                  value={requestData.requestedLoginTime}
                  onChange={(e) => setRequestData({ ...requestData, requestedLoginTime: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Requested Logout Time:</label>
                <input
                  type="time"
                  value={requestData.requestedLogoutTime}
                  onChange={(e) => setRequestData({ ...requestData, requestedLogoutTime: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Reason for Change:</label>
                <textarea
                  value={requestData.reason}
                  onChange={(e) => setRequestData({ ...requestData, reason: e.target.value })}
                  placeholder="Explain why you need this change..."
                  required
                ></textarea>
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn-submit">Submit Request</button>
                <button type="button" className="btn-cancel" onClick={() => setShowRequestModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Monthly Attendance Table */}
      <div className="dashboard-card history-card" style={{ overflowX: 'auto' }}>
        <div className="card-header-with-action">
          <div className="card-header-title-row">
            <h2>Attendance Record</h2>
            <Link
              to="/employee/attendance/requests"
              className="btn-all-requests"
            >
              All Requests
            </Link>
          </div>
          <div className="header-actions">
            <div className="control-group" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <label>Month:</label>
                <select value={filterMonth} onChange={(e) => setFilterMonth(parseInt(e.target.value))}>
                    {Array.from({ length: 12 }, (_, i) => (
                        <option key={i} value={i + 1}>{new Date(0, i).toLocaleString('default', { month: 'short' })}</option>
                    ))}
                </select>
            </div>
            <div className="control-group" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <label>Year:</label>
                <select value={filterYear} onChange={(e) => setFilterYear(parseInt(e.target.value))}>
                    {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
            </div>
          </div>
        </div>
        
        <div className="history-table-wrapper" style={{ marginTop: '1rem' }}>
          <table className="history-table attendance-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Clock In</th>
                <th>Clock Out</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
                {(() => {
                    const now = new Date();
                    now.setHours(0, 0, 0, 0);
                    let allRows = generateCalendarRows();
                    
                    // Filter out future dates so "latest" means today or the last day of a past month
                    let pastRows = allRows.filter(r => {
                        const rowDate = new Date(filterYear, filterMonth - 1, r.dayNum);
                        return rowDate <= now;
                    });
                    
                    // If they are looking at a future month, just use all rows
                    if (pastRows.length === 0) pastRows = allRows;

                    // Sort descending (latest at top)
                    pastRows.reverse();

                    let displayRows = showAllRecords ? pastRows : pastRows.slice(0, 5);

                    return displayRows.map(row => {
                        let rowStyle = {};
                        if (row.isSunday) rowStyle = { backgroundColor: '#fee2e2' }; 
                        if (row.isHoliday) rowStyle = { backgroundColor: '#fef3c7' }; 

                        return (
                        <tr key={row.dayNum} style={rowStyle} className={row.isPending ? 'row-pending' : ''}>
                            <td>
                                {row.date.split('-').reverse().join('/')}
                                {row.isPending && <div className="pending-badge" style={{ fontSize: '0.75rem', color: '#6366f1', fontWeight: 'bold' }}>Pending Request</div>}
                            </td>
                            <td>
                                {row.isAbsent ? (
                                    <span className="status-absent" style={{ color: 'red', fontWeight: 'bold' }}>Absent</span>
                                ) : (
                                    <span className="time-val">{row.clockIn}</span>
                                )}
                                {row.isPending && (
                                    <div className="requested-time-msg" style={{ fontSize: '0.75rem', color: '#6366f1', marginTop: '4px' }}>
                                        Req: <strong>{formatTime(row.entry.changeRequest.requestedLogin)}</strong>
                                    </div>
                                )}
                                {row.isSunday && <div style={{ fontSize: '0.75rem', color: '#b91c1c', fontWeight: 'bold' }}>Sunday</div>}
                                {row.isHoliday && <div style={{ fontSize: '0.75rem', color: '#b45309', fontWeight: 'bold' }}>Holiday ({row.holidayName})</div>}
                            </td>
                            <td>
                                {row.isAbsent ? (
                                    <span className="status-absent" style={{ color: 'red', fontWeight: 'bold' }}>Absent</span>
                                ) : (
                                    <span className="time-val">{row.clockOut}</span>
                                )}
                                {row.isPending && (
                                    <div className="requested-time-msg" style={{ fontSize: '0.75rem', color: '#6366f1', marginTop: '4px' }}>
                                        Req: <strong>{formatTime(row.entry.changeRequest.requestedLogout)}</strong>
                                    </div>
                                )}
                                {row.isSunday && <div style={{ fontSize: '0.75rem', color: '#b91c1c', fontWeight: 'bold' }}>Sunday</div>}
                                {row.isHoliday && <div style={{ fontSize: '0.75rem', color: '#b45309', fontWeight: 'bold' }}>Holiday</div>}
                            </td>
                            <td>
                                {!row.isPending && !row.isFuture && (
                                    <button className="btn-request" onClick={() => openRequestModal(row.entry || { date: row.date })} style={{ padding: '6px 12px', fontSize: '0.85rem', backgroundColor: '#6366f1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                        {row.hasEntry ? 'Request Change' : 'Add Time'}
                                    </button>
                                )}
                            </td>
                        </tr>
                    );
                });
                })()}
            </tbody>
          </table>
          <div style={{ textAlign: 'center', marginTop: '1rem', paddingBottom: '1rem' }}>
              <button 
                  onClick={() => setShowAllRecords(!showAllRecords)}
                  style={{ padding: '8px 24px', backgroundColor: '#0D47A1', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
                  {showAllRecords ? 'View Less' : 'View All'}
              </button>
          </div>
        </div>
      </div>
      {/* Verification Modal */}
      <VerifyPasswordModal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        onVerified={() => setShowSalary(true)}
        message="Please enter your password to view your salary information."
      />
    </div>
  );
};

export default EmployeeDashboard;
